import { useEffect, useRef, useState } from 'react';

import {
    FILE_PREPARATION_RETRY_INTERVAL_MS,
    formatCountdown,
    getOrCreatePreparationEndTime,
    getRemainingSeconds,
} from './filePreparationCountdown';

interface UseFilePreparationCountdownResult {
    /** Remaining seconds until the anchored countdown expires. */
    remainingSec: number;
    /** Human-readable m:ss countdown string. */
    formatted: string;
    /** True once the window has elapsed; ticking and retries are stopped. */
    expired: boolean;
}

/**
 * Cookie-anchored countdown per file_id (30-minute window). Reads the anchor
 * once on mount and ticks down; while the window is open it re-triggers
 * {@link onRetry} every {@link FILE_PREPARATION_RETRY_INTERVAL_MS}. When the
 * window elapses it sets `expired` and stops both the tick and the retries —
 * no renewal. The caller renders the generic error screen once `expired`.
 */
export const useFilePreparationCountdown = (
    fileId: string | undefined,
    onRetry: () => void,
): UseFilePreparationCountdownResult => {
    const onRetryRef = useRef(onRetry);
    onRetryRef.current = onRetry;

    const [remainingSec, setRemainingSec] = useState<number>(0);
    const [expired, setExpired] = useState<boolean>(false);

    useEffect(() => {
        if (!fileId) {
            return undefined;
        }

        setExpired(false);
        const endTime = getOrCreatePreparationEndTime(fileId);
        setRemainingSec(getRemainingSeconds(endTime));

        let retryId = 0;
        const tickId = window.setInterval((): void => {
            const remaining = getRemainingSeconds(endTime);
            setRemainingSec(remaining);
            if (remaining <= 0) {
                setExpired(true);
                window.clearInterval(tickId);
                window.clearInterval(retryId);
            }
        }, 1000);
        retryId = window.setInterval((): void => {
            onRetryRef.current();
        }, FILE_PREPARATION_RETRY_INTERVAL_MS);

        return (): void => {
            window.clearInterval(tickId);
            window.clearInterval(retryId);
        };
    }, [fileId]);

    return {
        remainingSec,
        formatted: formatCountdown(remainingSec),
        expired,
    };
};
